import type {ESPLoader, Transport} from 'esptool-js';
import {md5} from 'js-md5';

type FlashReadLoader = Pick<ESPLoader, 'checkCommand' | 'ESP_READ_FLASH'>;
type FlashReadTransport = Pick<Transport, 'read' | 'write'>;
type FlashReadProgress = (packet: Uint8Array, received: number, total: number) => void;

const packetSize = 0x1000;
const packetTimeout = 15000;
const maxFlashAddress = 0x1000000;

/** Complete the stub READ_FLASH exchange, including its final digest frame.
 * esptool-js 0.7.0's readFlash leaves that frame in flight, so a subsequent
 * command can arrive before the stub has returned to command/response mode.
 */
export async function readEsp32Flash(
  loader: FlashReadLoader,
  transport: FlashReadTransport,
  offset: number,
  size: number,
  onPacketReceived?: FlashReadProgress | null,
): Promise<Uint8Array> {
  if (!Number.isInteger(offset) || !Number.isInteger(size) || offset < 0 || size <= 0 || offset + size > maxFlashAddress) {
    throw new Error('Vùng đọc flash ESP32 không hợp lệ. Chưa ghi dữ liệu.');
  }
  const request = new Uint8Array(16);
  const requestView = new DataView(request.buffer);
  requestView.setUint32(0, offset, true);
  requestView.setUint32(4, size, true);
  requestView.setUint32(8, packetSize, true);
  // Stop after one packet until its cumulative ACK arrives. This works with
  // both stub interpretations of the window (packet count or byte count).
  requestView.setUint32(12, 1, true);
  let result: number | Uint8Array;
  try {
    result = await loader.checkCommand('read flash', loader.ESP_READ_FLASH, request);
  } catch {
    throw new Error('Không bắt đầu được đọc flash qua USB. Chưa ghi dữ liệu; kết nối lại rồi thử lại.');
  }
  if (result !== 0) throw new Error('ESP32 không chấp nhận đọc flash. Chưa ghi dữ liệu.');

  async function readPacket(): Promise<Uint8Array> {
    try {
      return await transport.read(packetTimeout);
    } catch {
      // Never include device bytes from library errors: a dump may contain
      // Wi-Fi credentials, tokens, and journals.
      throw new Error('Luồng đọc flash qua USB bị gián đoạn. Chưa ghi dữ liệu; kết nối lại rồi thử lại.');
    }
  }

  const data = new Uint8Array(size);
  for (let received = 0; received < size;) {
    const packet = await readPacket();
    const expected = Math.min(packetSize, size - received);
    if (!(packet instanceof Uint8Array) || packet.length !== expected) {
      throw new Error('Gói dữ liệu flash qua USB không đủ hoặc sai kích thước. Chưa ghi dữ liệu; kết nối lại rồi thử lại.');
    }
    data.set(packet, received);
    received += packet.length;
    const ack = new Uint8Array(4);
    new DataView(ack.buffer).setUint32(0, received, true);
    try {
      await transport.write(ack);
    } catch {
      throw new Error('Không xác nhận được dữ liệu flash qua USB. Chưa ghi dữ liệu; kết nối lại rồi thử lại.');
    }
    onPacketReceived?.(packet, received, size);
  }

  // The stub returns to normal command handling only after sending this raw
  // SLIP frame. Consume and verify it before allowing another READ_FLASH/MD5.
  const digest = await readPacket();
  if (!(digest instanceof Uint8Array) || digest.length !== 16) {
    throw new Error('ESP32 chưa gửi đủ mã kiểm chứng flash. Chưa ghi dữ liệu; kết nối lại rồi thử lại.');
  }
  const expectedDigest = [...digest].map(byte => byte.toString(16).padStart(2, '0')).join('');
  if (md5(data) !== expectedDigest) {
    throw new Error('Dữ liệu đọc qua USB không khớp mã kiểm chứng flash. Chưa ghi dữ liệu; kết nối lại rồi thử lại.');
  }
  return data;
}
